import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, User, DollarSign, Calendar, Clock, FileText, EuroIcon, Plus, Save, CheckCircle, ChevronDown, Check, X, Plane, FolderOpen, Star, Users, Settings } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { format, parseISO, startOfMonth, endOfMonth, eachWeekOfInterval, startOfWeek, endOfWeek, isWithinInterval, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TYPES_FACTURATION, CATEGORIES_TEMPS } from '../data/mockData';
import { getCurrentWeek, weekInputToDate, formatDateForAPI } from '../utils/dateUtils';
import VacancesModal from '../components/VacancesModal';
import { rattachementsService, personnesService, equipesService } from '../services/api';

const PersonneDetail = () => {
  const { id } = useParams();
  const { loadPersonneById, state, actions } = useApp();
  const [personne, setPersonne] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Modal state
  const [showTempsModal, setShowTempsModal] = useState(false);
  const [showVacancesModal, setShowVacancesModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedProjets, setSelectedProjets] = useState([]);
  const [tempsDataByProjet, setTempsDataByProjet] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [existingTempsByProjet, setExistingTempsByProjet] = useState({});
  const [projetsRattaches, setProjetsRattaches] = useState([]);
  
  // États pour la gestion des équipes
  const [equipesPersonne, setEquipesPersonne] = useState([]);
  const [showEquipesModal, setShowEquipesModal] = useState(false);
  const [equipesDisponibles, setEquipesDisponibles] = useState([]);
  const [selectedEquipeId, setSelectedEquipeId] = useState('');
  
  // États pour l'édition des infos de base
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editSaved, setEditSaved] = useState(false);

  useEffect(() => {
    const loadPersonne = async () => {
      setLoading(true);
      try {
        // Charger les données de la personne, ses rattachements et ses équipes en parallèle
        const [personneData, rattachementsResponse, equipesResponse] = await Promise.all([
          loadPersonneById(id),
          rattachementsService.getAll(),
          personnesService.getEquipes(id)
        ]);
        
        setPersonne(personneData);
        
        // Filtrer les rattachements pour cette personne et enrichir avec les données des projets
        const allRattachements = rattachementsResponse.data || [];
        const personneRattachements = allRattachements.filter(r => r.id_personne === parseInt(id));
        
        // Récupérer les projets depuis le state global pour enrichir les rattachements
        const projetsRattachesEnrichis = personneRattachements.map(rattachement => {
          const projet = state.projets?.data?.find(p => p.id === rattachement.id_projet);
          return {
            ...rattachement,
            projet: projet
          };
        }).filter(r => r.projet && r.projet.statut === 'en_cours'); // Filtrer uniquement les projets actifs
        
        setProjetsRattaches(projetsRattachesEnrichis);
        
        // Charger les équipes de la personne
        if (equipesResponse.success) {
          setEquipesPersonne(equipesResponse.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadPersonne();
    }
  }, [id, loadPersonneById, state.projets]); // Inclure state.projets pour recharger si les projets changent

  useEffect(() => {
    console.log(personne);
    // Initialiser les données d'édition quand la personne change
    if (personne) {
      setEditFormData({
        email: personne.email || '',
        taux_journalier: personne.taux_journalier || '',
        // Convertir le ratio en pourcentage (0.5 → 50)
        indice_productivite: personne.indice_productivite ? (parseFloat(personne.indice_productivite) * 100).toString() : ''
      });
    }
  }, [personne]);

  // Reset form when modal opens
  useEffect(() => {
    if (showTempsModal) {
      setSelectedProjets([]);
      setTempsDataByProjet({});
      setErrors({});
      setSaved(false);
      
      // Load temps consommés for the display
      if (!state.tempsConsommes?.data) {
        actions.loadAllTempsConsommes({ limit: 100 });
      }
    }
  }, [showTempsModal]);

  // Initialize temps data when projets selection changes
  useEffect(() => {
    setTempsDataByProjet(prev => {
      const newTempsData = { ...prev };
      
      // Add data for newly selected projets
      selectedProjets.forEach(projetId => {
        if (!newTempsData[projetId]) {
          const projet = state.projets?.data?.find(p => p.id === parseInt(projetId));
          newTempsData[projetId] = {
            jours_consommes: '',
            facturable: projet?.facturable ?? true,
            commentaire: '',
            phase: 'build'
          };
        }
      });
      
      // Remove data for unselected projets
      Object.keys(newTempsData).forEach(projetId => {
        if (!selectedProjets.includes(projetId)) {
          delete newTempsData[projetId];
        }
      });
      
      return newTempsData;
    });
  }, [selectedProjets, state.projets]);

  // Update existing temps when week or projets selection changes in modal
  useEffect(() => {
    if (showTempsModal && selectedWeek && selectedProjets.length > 0) {
      const existingTemps = {};
      selectedProjets.forEach(projetId => {
        existingTemps[projetId] = getExistingTempsForProjetWeek(projetId, selectedWeek);
      });
      setExistingTempsByProjet(existingTemps);
    } else {
      setExistingTempsByProjet({});
    }
  }, [selectedWeek, selectedProjets, state.tempsConsommes, showTempsModal]);

  // Close dropdown when clicking outside (modal version)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTempsModal && !event.target.closest('.relative')) {
        setIsDropdownOpen(false);
      }
    };

    if (showTempsModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTempsModal]);

  // Function to get existing temps for a projet and week in modal
  const getExistingTempsForProjetWeek = (projetId, week) => {
    if (!state.tempsConsommes?.data || !week) return [];
    
    const weekDate = weekInputToDate(week);
    const apiWeekFormat = formatDateForAPI(weekDate);
    
    return state.tempsConsommes.data.filter(temps => 
      temps.id_projet === parseInt(projetId) && 
      temps.id_personne === parseInt(id) &&
      temps.semaine === apiWeekFormat
    );
  };

  const handleTempsChange = (projetId, fieldName, value) => {
    setTempsDataByProjet(prev => ({
      ...prev,
      [projetId]: {
        ...prev[projetId],
        [fieldName]: value
      }
    }));
    
    // Clear related errors
    const errorKey = `${projetId}_${fieldName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
    setSaved(false);
  };

  const toggleProjet = (projetId) => {
    setSelectedProjets(prev => {
      const isSelected = prev.includes(projetId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== projetId)
        : [...prev, projetId];
      return newSelection;
    });
    setErrors(prev => ({ ...prev, projets: '' }));
  };

  const toggleSelectAll = () => {
    const activeProjets = state.projets?.data?.filter(p => p.statut === 'en_cours') || [];
    if (selectedProjets.length === activeProjets.length) {
      setSelectedProjets([]);
    } else {
      setSelectedProjets(activeProjets.map(p => p.id.toString()));
    }
    setErrors(prev => ({ ...prev, projets: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedProjets.length === 0) {
      newErrors.projets = 'Sélectionnez au moins un projet';
    }
    
    if (!selectedWeek) {
      newErrors.semaine = 'Sélectionnez une semaine';
    }
    
    // Validate each projet's data
    selectedProjets.forEach(projetId => {
      const tempsData = tempsDataByProjet[projetId];
      if (!tempsData) return;
      
      const joursConsommes = parseFloat(tempsData.jours_consommes);
      if (!tempsData.jours_consommes || joursConsommes <= 0) {
        newErrors[`${projetId}_jours_consommes`] = 'Les jours consommés doivent être supérieurs à 0';
      } else if (joursConsommes > 5) {
        newErrors[`${projetId}_jours_consommes`] = 'Les jours consommés ne peuvent pas dépasser 5 par semaine';
      }
      
      if (!tempsData.commentaire.trim()) {
        newErrors[`${projetId}_commentaire`] = 'Le commentaire est requis';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveTemps = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Convert week format to Monday date for API
      const weekDate = weekInputToDate(selectedWeek);
      const apiWeekFormat = formatDateForAPI(weekDate);
      
      // Save temps consommés for each selected projet
      const savePromises = selectedProjets.map(projetId => {
        const tempsData = tempsDataByProjet[projetId];
        return actions.saveTempsConsomme({
          id_projet: parseInt(projetId),
          id_personne: parseInt(id),
          semaine: apiWeekFormat,
          jours_consommes: parseFloat(tempsData.jours_consommes),
          facturable: tempsData.facturable,
          commentaire: tempsData.commentaire,
          phase: tempsData.phase
        });
      });

      await Promise.all(savePromises);

      // Reload person data and projects to update the display
      const [personneData, rattachementsResponse] = await Promise.all([
        loadPersonneById(id),
        rattachementsService.getAll()
      ]);
      setPersonne(personneData);
      
      // Update projets rattachés as well
      const allRattachements = rattachementsResponse.data || [];
      const personneRattachements = allRattachements.filter(r => r.id_personne === parseInt(id));
      const projetsRattachesEnrichis = personneRattachements.map(rattachement => {
        const projet = state.projets?.data?.find(p => p.id === rattachement.id_projet);
        return {
          ...rattachement,
          projet: projet
        };
      }).filter(r => r.projet && r.projet.statut === 'en_cours');
      setProjetsRattaches(projetsRattachesEnrichis);

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setShowTempsModal(false);
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const openTempsModal = (weekStart) => {
    // weekStart is already a Date object representing the Monday of the week
    const year = weekStart.getFullYear();
    const weekNumber = getISOWeek(weekStart);
    const weekString = `${year}-W${weekNumber.toString().padStart(2, '0')}`;
    setSelectedWeek(weekString);
    setShowTempsModal(true);
  };

  // Fonctions pour la gestion des équipes
  const loadEquipesDisponibles = async () => {
    try {
      const response = await equipesService.getMesEquipes();
      if (response.success) {
        // Filtrer les équipes où la personne n'est pas déjà membre
        const equipesDejaMembre = equipesPersonne.map(eq => eq.equipe.id);
        const equipesFiltered = response.data.filter(eq => !equipesDejaMembre.includes(eq.id));
        setEquipesDisponibles(equipesFiltered);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des équipes:', error);
    }
  };

  const handleOpenEquipesModal = () => {
    loadEquipesDisponibles();
    setShowEquipesModal(true);
  };

  const handleAddToEquipe = async () => {
    if (!selectedEquipeId) return;

    try {
      await personnesService.addToEquipe(id, {
        equipe_id: parseInt(selectedEquipeId),
        role_code: 'membre'
      });
      
      // Recharger les équipes de la personne
      const equipesResponse = await personnesService.getEquipes(id);
      if (equipesResponse.success) {
        setEquipesPersonne(equipesResponse.data || []);
      }
      
      setShowEquipesModal(false);
      setSelectedEquipeId('');
    } catch (error) {
      console.error('Erreur lors de l\'ajout à l\'équipe:', error);
      alert('Erreur lors de l\'ajout à l\'équipe');
    }
  };

  const handleRemoveFromEquipe = async (equipeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cette personne de l\'équipe ?')) {
      return;
    }

    try {
      await personnesService.removeFromEquipe(id, equipeId);
      
      // Recharger les équipes de la personne
      const equipesResponse = await personnesService.getEquipes(id);
      if (equipesResponse.success) {
        setEquipesPersonne(equipesResponse.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du retrait de l\'équipe:', error);
      alert('Erreur lors du retrait de l\'équipe');
    }
  };

  // Fonctions pour la gestion de l'édition des infos de base
  const handleOpenEditModal = () => {
    setEditFormData({
      email: personne.email || '',
      taux_journalier: personne.taux_journalier || '',
      // Convertir le ratio en pourcentage (0.5 → 50)
      indice_productivite: personne.indice_productivite ? (parseFloat(personne.indice_productivite) * 100).toString() : ''
    });
    setEditErrors({});
    setEditSaved(false);
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editFormData.email || !emailRegex.test(editFormData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide';
    }

    // Validation taux journalier
    const tauxNum = parseFloat(editFormData.taux_journalier);
    if (!editFormData.taux_journalier || isNaN(tauxNum) || tauxNum < 0) {
      newErrors.taux_journalier = 'Le taux journalier doit être un nombre positif ou zéro';
    }

    // Validation indice de productivité (0-100 en pourcentage)
    const indiceNum = parseFloat(editFormData.indice_productivite);
    if (!editFormData.indice_productivite || isNaN(indiceNum) || indiceNum < 0 || indiceNum > 100) {
      newErrors.indice_productivite = 'L\'indice de productivité doit être entre 0 et 100 %';
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEditForm()) {
      return;
    }

    try {
      const updateData = {
        email: editFormData.email,
        taux_journalier: parseFloat(editFormData.taux_journalier),
        // Convertir le pourcentage en ratio (50 → 0.5)
        indice_productivite: parseFloat(editFormData.indice_productivite) / 100
      };

      await personnesService.update(id, updateData);
      
      // Recharger les données de la personne
      const personneData = await loadPersonneById(id);
      setPersonne(personneData);

      setEditSaved(true);
      setTimeout(() => {
        setEditSaved(false);
        setShowEditModal(false);
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setEditErrors({ submit: 'Erreur lors de la sauvegarde des modifications' });
    }
  };

  // Fonction pour obtenir les semaines du mois sélectionné
  const getWeeksInMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 }); // Semaine commence le lundi
  };

  // Fonction pour obtenir les temps consommés d'une semaine
  const getTempsForWeek = (weekStart) => {
    if (!personne?.temps_consommes) return [];
    
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    
    return personne.temps_consommes.filter(temps => {
      const tempsDate = parseISO(temps.semaine);
      return isWithinInterval(tempsDate, { start: weekStart, end: weekEnd });
    });
  };

  // Calcul des statistiques pour le mois sélectionné
  const getMonthStats = (selectedDate) => {
    if (!personne?.temps_consommes) return { totalJours: 0, totalFacturable: 0, projets: new Set() };
    
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    
    // Filtrer les temps consommés du mois sélectionné
    const tempsOfMonth = personne.temps_consommes.filter(temps => {
      const tempsDate = parseISO(temps.semaine);
      return isWithinInterval(tempsDate, { start: monthStart, end: monthEnd });
    });
    
    return tempsOfMonth.reduce((acc, temps) => {
      const jours = parseFloat(temps.jours_consommes);
      acc.totalJours += jours;
      if (temps.facturable) acc.totalFacturable += jours;
      acc.projets.add(temps.projet.nom);
      return acc;
    }, { totalJours: 0, totalFacturable: 0, projets: new Set() });
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!personne) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Personne non trouvée</p>
          <Link to="/personnes" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
            Retour aux personnes
          </Link>
        </div>
      </div>
    );
  }

  const weeks = getWeeksInMonth(selectedMonth);
  const stats = getMonthStats(selectedMonth);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex items-center mb-6">
        <Link
          to="/personnes"
          className="flex items-center text-gray-500 hover:text-gray-700 mr-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{personne.prenom} {personne.nom}</h1>
      </div>

      {/* Informations de base */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-semibold text-gray-900">{personne.prenom} {personne.nom}</h2>
              <p className="text-gray-600 capitalize">{personne.role}</p>
            </div>
          </div>
          <button
            onClick={handleOpenEditModal}
            className="flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Settings className="h-4 w-4 mr-1" />
            Modifier
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{personne.email}</p>
            </div>
          </div>
          <div className="flex items-center">
            <DollarSign className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Taux journalier</p>
              <p className="font-medium">{personne.taux_journalier} €/jour</p>
            </div>
          </div>
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Indice productivité</p>
              <p className="font-medium">{parseFloat(personne.indice_productivite * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Équipes de la personne */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Users className="h-6 w-6 text-gray-400 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">
              Équipes ({equipesPersonne.length})
            </h3>
          </div>
          <button
            onClick={handleOpenEquipesModal}
            className="flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter à une équipe
          </button>
        </div>
        
        {equipesPersonne.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {equipesPersonne.map((appartenance) => (
              <div key={appartenance.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: appartenance.equipe.couleur }}
                    >
                      {appartenance.equipe.nom.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {appartenance.equipe.nom}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {appartenance.equipe.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveFromEquipe(appartenance.equipe.id)}
                    className="text-red-400 hover:text-red-600 p-1"
                    title="Retirer de l'équipe"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Rôle :</span>
                  <span 
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${appartenance.role?.couleur || '#6B7280'}20`,
                      color: appartenance.role?.couleur || '#6B7280'
                    }}
                  >
                    {appartenance.role?.nom || 'Membre'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Cette personne n'est membre d'aucune équipe</p>
            <button
              onClick={handleOpenEquipesModal}
              className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Ajouter à une équipe
            </button>
          </div>
        )}
      </div>

      {/* Projets actifs rattachés */}
      {projetsRattaches.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center mb-4">
            <FolderOpen className="h-6 w-6 text-gray-400 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">
              Projets actifs ({projetsRattaches.length})
            </h3>
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projetsRattaches.map((rattachement) => (
              <div key={rattachement.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      {rattachement.projet.nom}
                    </h4>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {rattachement.projet.description}
                    </p>
                  </div>
                  {rattachement.projet.facturable && (
                    <Star className="h-4 w-4 text-yellow-500 ml-2 flex-shrink-0" title="Projet facturable" />
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Rôle :</span>
                    <span className="font-medium text-gray-900 capitalize">
                      {rattachement.role_projet?.replace('_', ' ') || 'Non défini'}
                    </span>
                  </div>
                  
                  {rattachement.phase && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Phase :</span>
                      <span className="font-medium text-gray-900 capitalize">
                        {rattachement.phase.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Client :</span>
                    <span className="font-medium text-gray-900">
                      {rattachement.projet.client || 'Non défini'}
                    </span>
                  </div>
                  
                  {rattachement.date_debut && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Depuis :</span>
                      <span className="font-medium text-gray-900">
                        {format(parseISO(rattachement.date_debut), 'dd/MM/yyyy', { locale: fr })}
                      </span>
                    </div>
                  )}
                  
                  {rattachement.date_fin && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Jusqu'au :</span>
                      <span className="font-medium text-gray-900">
                        {format(parseISO(rattachement.date_fin), 'dd/MM/yyyy', { locale: fr })}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Link
                    to={`/projets/${rattachement.projet.id}`}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Voir le projet →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistiques rapides pour le mois sélectionné */}
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Statistiques pour {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-900">Total jours</p>
              <p className="text-2xl font-bold text-blue-900">{stats.totalJours}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-900">Jours facturables</p>
              <p className="text-2xl font-bold text-green-900">{stats.totalFacturable}</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-900">Projets actifs</p>
              <p className="text-2xl font-bold text-purple-900">{stats.projets.size}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendrier des temps */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Calendar className="h-6 w-6 text-gray-400 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Temps consommés</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              ←
            </button>
            <span className="text-sm font-medium text-gray-900 min-w-[120px] text-center">
              {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
            </span>
            <button
              onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              →
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {weeks.map((weekStart, index) => {
            const tempsWeek = getTempsForWeek(weekStart);
            const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
            const totalJoursWeek = tempsWeek.reduce((sum, temps) => sum + parseFloat(temps.jours_consommes), 0);

            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900">
                      {format(weekStart, 'dd MMM', { locale: fr })} - {format(weekEnd, 'dd MMM', { locale: fr })}
                    </span>
                    {totalJoursWeek > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {totalJoursWeek} jour{totalJoursWeek > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openTempsModal(weekStart)}
                      className="flex items-center px-2 py-1 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                      title="Ajouter du temps pour cette semaine"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Temps
                    </button>
                    <button
                      onClick={() => setShowVacancesModal(true)}
                      className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                      title="Gérer les vacances"
                    >
                      <Plane className="h-3 w-3 mr-1" />
                      Vacances
                    </button>
                  </div>
                </div>

                {tempsWeek.length > 0 ? (
                  <div className="space-y-2">
                    {tempsWeek.map((temps) => (
                      <div key={temps.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">{temps.projet.nom}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              temps.facturable 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {temps.facturable ? 'Facturable' : 'Non facturable'}
                            </span>
                            {temps.phase && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.color || 'bg-gray-100 text-gray-800'
                              }`}>
                                {CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase}
                              </span>
                            )}
                            <span className="text-sm text-gray-500">
                              {temps.jours_consommes} jour{parseFloat(temps.jours_consommes) > 1 ? 's' : ''}
                            </span>
                          </div>
                          {temps.commentaire && (
                            <p className="text-sm text-left text-gray-600 mt-1">{temps.commentaire}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucun temps consommé cette semaine</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de saisie de temps */}
      {showTempsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowTempsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Ajouter du temps - {personne.prenom} {personne.nom}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Semaine du {selectedWeek && format(weekInputToDate(selectedWeek), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {saved && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">Sauvegardé</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowTempsModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Formulaire */}
              <div className="space-y-6">
                {/* Contexte de saisie */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Semaine *</label>
                    <input
                      type="week"
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(e.target.value)}
                      className={`form-input ${errors.semaine ? 'border-red-500' : ''}`}
                    />
                    {errors.semaine && (
                      <p className="mt-1 text-sm text-red-600">{errors.semaine}</p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="form-label">Projets *</label>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`form-input w-full text-left flex items-center justify-between ${errors.projets ? 'border-red-500' : ''}`}
                    >
                      <span className="text-gray-700">
                        {selectedProjets.length === 0
                          ? 'Sélectionnez des projets'
                          : `${selectedProjets.length} projet(s) sélectionné(s)`
                        }
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {/* Select All option */}
                        <div 
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          onClick={toggleSelectAll}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                            {selectedProjets.length === state.projets?.data?.filter(p => p.statut === 'en_cours').length && 
                             selectedProjets.length > 0 && (
                              <Check className="h-3 w-3 text-primary-600" />
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            Tout sélectionner
                          </span>
                        </div>

                        {/* Individual options */}
                        {state.projets?.data?.filter(p => p.statut === 'en_cours').map(projet => (
                          <div 
                            key={projet.id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleProjet(projet.id.toString())}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                              {selectedProjets.includes(projet.id.toString()) && (
                                <Check className="h-3 w-3 text-primary-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm text-gray-900">
                                {projet.nom}
                              </span>
                              <span className={`text-xs ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full ${
                                projet.facturable 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {projet.facturable ? 'Facturable' : 'Non fact.'}
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Empty state */}
                        {(!state.projets?.data?.filter(p => p.statut === 'en_cours') || state.projets.data.filter(p => p.statut === 'en_cours').length === 0) && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            Aucun projet en cours disponible
                          </div>
                        )}
                      </div>
                    )}

                    {errors.projets && (
                      <p className="mt-1 text-sm text-red-600">{errors.projets}</p>
                    )}
                    {selectedProjets.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedProjets.map(projetId => {
                          const projet = state.projets?.data?.find(p => p.id.toString() === projetId);
                          return projet ? (
                            <span 
                              key={projetId}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {projet.nom}
                              <button
                                type="button"
                                onClick={() => toggleProjet(projetId)}
                                className="ml-1 h-3 w-3 rounded-full hover:bg-primary-200 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Temps consommés */}
                {selectedProjets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Sélectionnez des projets pour saisir vos temps</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedProjets.map((projetId) => {
                      const projet = state.projets?.data?.find(p => p.id === parseInt(projetId));
                      const tempsData = tempsDataByProjet[projetId] || {};
                      
                      return (
                        <div key={projetId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center mb-4">
                            <Calendar className="h-5 w-5 text-primary-600 mr-2" />
                            <h4 className="text-lg font-medium text-gray-900">
                              {projet?.nom}
                            </h4>
                            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                              projet?.facturable 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {projet?.facturable ? 'Facturable' : 'Non facturable'}
                            </span>
                          </div>

                          {/* Affichage des temps existants pour ce projet et cette semaine */}
                          {existingTempsByProjet[projetId] && existingTempsByProjet[projetId].length > 0 && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <h5 className="text-sm font-medium text-blue-800 mb-2">
                                Temps déjà saisis pour cette semaine :
                              </h5>
                              <div className="space-y-2">
                                {existingTempsByProjet[projetId].map((temps, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-blue-700 font-medium">
                                        {temps.jours_consommes} jour{temps.jours_consommes > 1 ? 's' : ''}
                                      </span>
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                        temps.facturable 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {temps.facturable ? 'Facturable' : 'Non fact.'}
                                      </span>
                                      {temps.phase && (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                          CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.color || 'bg-gray-100 text-gray-600'
                                        }`}>
                                          {CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase}
                                        </span>
                                      )}
                                    </div>
                                    {temps.commentaire && (
                                      <span className="text-blue-600 text-xs" title={temps.commentaire}>
                                        💬
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div>
                              <label className="form-label">Jours consommés *</label>
                              <input
                                type="number"
                                value={tempsData.jours_consommes || ''}
                                onChange={(e) => handleTempsChange(projetId, 'jours_consommes', e.target.value)}
                                step="0.5"
                                min="0"
                                max="5"
                                className={`form-input ${errors[`${projetId}_jours_consommes`] ? 'border-red-500' : ''}`}
                                placeholder="0.0"
                              />
                              {errors[`${projetId}_jours_consommes`] && (
                                <p className="mt-1 text-sm text-red-600">{errors[`${projetId}_jours_consommes`]}</p>
                              )}
                            </div>

                            <div>
                              <label className="form-label">Phase *</label>
                              <select
                                value={tempsData.phase || 'build'}
                                onChange={(e) => handleTempsChange(projetId, 'phase', e.target.value)}
                                className="form-input"
                              >
                                {CATEGORIES_TEMPS.map((categorie) => (
                                  <option key={categorie.value} value={categorie.value}>
                                    {categorie.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="form-label">Type de facturation</label>
                              <select
                                value={tempsData.facturable}
                                onChange={(e) => handleTempsChange(projetId, 'facturable', e.target.value === 'true')}
                                className="form-input"
                              >
                                {TYPES_FACTURATION.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-sm text-gray-500">
                                {tempsData.facturable 
                                  ? 'Ce temps sera facturé au client'
                                  : 'Temps interne non facturable'
                                }
                              </p>
                            </div>

                            <div>
                              <label className="form-label">Montant estimé</label>
                              <div className="mt-1 p-3 bg-white border border-gray-300 rounded-md">
                                <span className="text-sm font-medium text-gray-900">
                                  {personne && tempsData.jours_consommes && tempsData.facturable
                                    ? `${(parseFloat(tempsData.jours_consommes) * parseFloat(personne.taux_journalier)).toFixed(2)}€`
                                    : '0€'
                                  }
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="form-label">Commentaire *</label>
                            <textarea
                              value={tempsData.commentaire || ''}
                              onChange={(e) => handleTempsChange(projetId, 'commentaire', e.target.value)}
                              className={`form-input ${errors[`${projetId}_commentaire`] ? 'border-red-500' : ''}`}
                              rows="3"
                              placeholder="Décrivez le travail de la semaine..."
                            />
                            {errors[`${projetId}_commentaire`] && (
                              <p className="mt-1 text-sm text-red-600">{errors[`${projetId}_commentaire`]}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowTempsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleSaveTemps}
                  disabled={selectedProjets.length === 0 || !selectedWeek}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestion des vacances */}
      <VacancesModal
        isOpen={showVacancesModal}
        onClose={() => setShowVacancesModal(false)}
        personne={personne}
        onVacancesUpdated={async () => {
          // Recharger les données de la personne pour refléter les changements
          if (personne) {
            const [personneData, rattachementsResponse] = await Promise.all([
              loadPersonneById(id),
              rattachementsService.getAll()
            ]);
            setPersonne(personneData);
            
            // Update projets rattachés as well
            const allRattachements = rattachementsResponse.data || [];
            const personneRattachements = allRattachements.filter(r => r.id_personne === parseInt(id));
            const projetsRattachesEnrichis = personneRattachements.map(rattachement => {
              const projet = state.projets?.data?.find(p => p.id === rattachement.id_projet);
              return {
                ...rattachement,
                projet: projet
              };
            }).filter(r => r.projet && r.projet.statut === 'en_cours');
            setProjetsRattaches(projetsRattachesEnrichis);
          }
        }}
      />

      {/* Modal de gestion des équipes */}
      {showEquipesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Ajouter à une équipe</h3>
              <button 
                onClick={() => setShowEquipesModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {equipesDisponibles.length > 0 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionner une équipe
                  </label>
                  <select
                    value={selectedEquipeId}
                    onChange={(e) => setSelectedEquipeId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choisir une équipe...</option>
                    {equipesDisponibles.map((equipe) => (
                      <option key={equipe.id} value={equipe.id}>
                        {equipe.nom}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEquipesModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddToEquipe}
                    disabled={!selectedEquipeId}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm mb-4">
                  Aucune équipe disponible ou cette personne est déjà membre de toutes vos équipes.
                </p>
                <button
                  onClick={() => setShowEquipesModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de modification des infos de base */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Modifier les informations</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {personne?.prenom} {personne?.nom}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {editSaved && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">Enregistré</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Formulaire */}
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className={`form-input ${editErrors.email ? 'border-red-500' : ''}`}
                    placeholder="example@email.com"
                  />
                  {editErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{editErrors.email}</p>
                  )}
                </div>

                {/* Taux journalier */}
                <div>
                  <label className="form-label">Taux journalier (€) *</label>
                  <input
                    type="number"
                    value={editFormData.taux_journalier || ''}
                    onChange={(e) => handleEditChange('taux_journalier', e.target.value)}
                    className={`form-input ${editErrors.taux_journalier ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  {editErrors.taux_journalier && (
                    <p className="mt-1 text-sm text-red-600">{editErrors.taux_journalier}</p>
                  )}
                </div>

                {/* Indice de productivité */}
                <div>
                  <label className="form-label">Indice de productivité (%) (0-100) *</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={editFormData.indice_productivite || ''}
                      onChange={(e) => handleEditChange('indice_productivite', e.target.value)}
                      className={`form-input ${editErrors.indice_productivite ? 'border-red-500' : ''}`}
                      placeholder="100"
                      step="1"
                      min="0"
                      max="100"
                    />
                    <span className="text-sm text-gray-600">
                      {editFormData.indice_productivite && !editErrors.indice_productivite 
                        ? `${parseFloat(editFormData.indice_productivite).toFixed(0)}%`
                        : '-'
                      }
                    </span>
                  </div>
                  {editErrors.indice_productivite && (
                    <p className="mt-1 text-sm text-red-600">{editErrors.indice_productivite}</p>
                  )}
                </div>

                {editErrors.submit && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{editErrors.submit}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleSaveEdit}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonneDetail; 